// MS388 (Sprint 467, 2026-05-20): on-save title-casing for office
// addresses. Buyers (and Google Places autocomplete on some queries)
// regularly write addresses like "11 wilson street, south yarra 3141".
// The display surfaces (Settings, Order pickers, Vendor brief) render
// the raw stored string, so the lowercase suburb leaks into the
// product and reads as sloppy.
//
// Why a save-time normalizer and not an on-keystroke transform:
//   - Mutating input mid-type fights the user — they tab away from the
//     field and the cursor jumps, autofill flickers, etc.
//   - One pass on submit is the smallest change to ship the fix and
//     keeps the input field a controlled mirror of the user's text.
//
// What's preserved verbatim:
//   - All-digit tokens (postcodes "3141", street numbers "11", unit
//     numbers "42"). These should never be title-cased.
//   - Tokens that already contain a digit ("1st", "21B"). Leave them
//     alone — the user's specific casing is intent.
//   - Tokens that are already mixed-case ("McDonald", "O'Brien").
//     Re-title-casing would clobber the apostrophe and the cap, so
//     we only touch all-lowercase or all-uppercase tokens.
//   - Common lower-case connector words ("of", "and", "the", "at",
//     "by", "in", "on", "for"). Leaving them as-is matches Australian
//     suburb style ("Bay of Fires", "End of the Road"). This is purely
//     cosmetic so a small exclusion list is fine.
//
// Comma + whitespace handling: the input is split on the comma, each
// chunk is normalised, then joined with ", ". This collapses any
// "south yarra,3141" → "South Yarra, 3141" inconsistencies too, which
// is a nice-to-have side effect.

const SMALL_WORDS: ReadonlySet<string> = new Set([
  "of",
  "and",
  "the",
  "at",
  "by",
  "in",
  "on",
  "for",
]);

function isAllDigits(s: string): boolean {
  return /^\d+$/.test(s);
}

function containsDigit(s: string): boolean {
  return /\d/.test(s);
}

function isAllLowerOrAllUpper(s: string): boolean {
  const letters = s.replace(/[^a-zA-Z]/g, "");
  if (!letters) return false;
  return letters === letters.toLowerCase() || letters === letters.toUpperCase();
}

/**
 * Capitalise the first letter only; leave the rest as-is. We do this
 * (rather than `s.toLowerCase()` on the tail) so an explicit "McDonald"
 * stays as "McDonald" once we've decided to touch it — though in
 * practice we won't touch it, the isAllLowerOrAllUpper guard skips
 * mixed-case input.
 */
function capitaliseFirst(s: string): string {
  if (s.length === 0) return s;
  return s[0].toUpperCase() + s.slice(1).toLowerCase();
}

function titleCaseToken(token: string, isFirst: boolean): string {
  if (!token) return token;
  // Digits are pass-through (postcodes, street numbers).
  if (isAllDigits(token)) return token;
  // Anything with a digit ("1st", "21B") is user intent — pass through.
  if (containsDigit(token)) return token;
  // Mixed-case ("McDonald", "O'Brien") is user intent — pass through.
  if (!isAllLowerOrAllUpper(token)) return token;
  // Small words stay lower-case UNLESS they're the first token of a
  // segment (e.g. "Of Counsel" reads correctly when it leads).
  if (!isFirst && SMALL_WORDS.has(token.toLowerCase())) {
    return token.toLowerCase();
  }
  return capitaliseFirst(token);
}

/**
 * Title-case a single segment of an address (one chunk between commas).
 * Words are split on whitespace; multi-space runs are collapsed to a
 * single space in the output.
 */
function titleCaseSegment(segment: string): string {
  const trimmed = segment.trim();
  if (!trimmed) return "";
  return trimmed
    .split(/\s+/)
    .map((token, idx) => titleCaseToken(token, idx === 0))
    .join(" ");
}

/**
 * Normalise an office address string on SAVE:
 *   - Title-case street + suburb words
 *   - Preserve postcodes (all-digit tokens) verbatim
 *   - Don't touch mixed-case names (McDonald, O'Brien) or tokens with
 *     digits (1st, 21B)
 *   - Collapse "x,y" → "x, y" comma spacing as a side effect
 *
 * Empty or whitespace-only input returns "". The function is pure +
 * idempotent: normalize(normalize(x)) === normalize(x).
 */
export function normalizeAddressString(raw: string): string {
  if (!raw) return "";
  // Split on comma, normalise each chunk, drop empties, rejoin with
  // ", ". This handles "x,y" → "X, Y" and trailing commas.
  const parts = raw.split(",").map(titleCaseSegment).filter(Boolean);
  return parts.join(", ");
}
